import {LeftOutlined, RightOutlined} from '@ant-design/icons';
import {Button} from 'antd';
import PropTypes from 'prop-types';
import {useRef, useState} from 'react';
import {Link} from 'react-router-dom';

const RelatedProducts = ({products, currentProduct}) => {
  const [activeImages, setActiveImages] = useState({});
  const scrollRef = useRef(null);

  const related = products
    ?.filter(
      item =>
        item.category === currentProduct.category &&
        item.id !== currentProduct.id,
    )
    .slice(0, 12);

  const handleVariantImageChange = (productId, images) => {
    if (!images || images.length === 0) {return;}

    setActiveImages(prev => ({
      ...prev,
      [productId]: images[0],
    }));
  };
  const toDropboxRawUrl = (url = '') =>
    url.replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      .replace('?dl=0', '');
  const scrollByCards = direction => {
    const container = scrollRef.current;
    if (!container) {return;}

    const card = container.querySelector('[data-card]');
    const cardWidth = card?.offsetWidth || 300;

    container.scrollBy({
      left: direction * cardWidth * 1,
      behavior: 'smooth',
    });
  };

  return (
    <div className={'w-full mt-16 lg:mb-10 mb-5'}>
      <h1 className={'header'}>You May Like These...</h1>

      <div className={'section mt-4'}>
        {/* Wrapper for arrows + products */}
        <div className={'flex items-center gap-3'}>
          
          {/* LEFT ARROW (Desktop only) */}
          <button
            onClick={() => scrollByCards(-1)}
            className={'hidden lg:flex items-center justify-center w-8 h-8 rounded-full  shadow hover:bg-gray-100'}
          >
            <LeftOutlined className={'text-primary-900 text-xl font-bold'} />
          </button>

          {/* PRODUCTS SCROLLER */}
          <div
            ref={scrollRef}
            className={'flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth flex-1'}
          >
            {related.map(item => (
              <Link
                key={item.id}
                to={`/product/${item.id}`}
                state={{product: item}}
                data-card
                className={'min-w-[70%] sm:min-w-[45%] md:min-w-[30%] lg:min-w-[23%]'}
              >
                <div className={'shadow-md p-3 rounded-lg flex flex-col h-full'}>
                  <img
                    src={toDropboxRawUrl(activeImages[item.id] ||
                      (Array.isArray(item.variants?.[0]?.images)
                        ? item.variants[0].images[0]
                        : item.imageUrl))
                    }
                    alt={item.productName}
                    className={'w-full h-56 md:h-70 object-cover rounded-lg'}
                  />

                  {/* Color Switcher */}
                  {item.variants?.length > 0 && (
                    <div className={'flex gap-2 mt-3 pl-2'}>
                      {item.variants.map((variant, idx) => {
                        const hasImages = variant.images?.length > 0;
                        const isActive =
                          activeImages[item.id] === variant.images?.[0];

                        return (
                          <button
                            key={idx}
                            type={'button'}
                            title={variant.color}
                            disabled={!hasImages}
                            onMouseEnter={() =>
                              handleVariantImageChange(item.id, variant.images)
                            }
                            onClick={() =>
                              handleVariantImageChange(item.id, variant.images)
                            }
                            className={`w-5 h-5 rounded-full border transition-all duration-200
                              ${isActive ? 'border-primary-900 scale-110' : 'border-gray-300'}
                              ${!hasImages ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                            style={{backgroundColor: variant.color}}
                          />
                        );
                      })}
                    </div>
                  )}

                  <p className={'mt-2 text-sm font-serif font-semibold line-clamp-2'}>
                    {item.productName}
                  </p>

                  <div className={'flex flex-row pl-2 gap-2 mb-2 mt-2'}>
                    <p className={'font-semibold md:text-xl text-lg'}>
                      ₹{Math.round(item.price - (item.price * item.discount) / 100)}
                    </p>

                    {item.discount > 0 && (
                      <div className={'flex gap-2'}>
                        <p className={'line-through md:font-normal font-extralight text-gray'}>
                          ₹{item.price}
                        </p>
                        <span className={'bg-red-600 flex justify-center items-center text-white px-2 rounded-full md:text-sm'}>
                          -{item.discount}%
                        </span>
                      </div>
                    )}
                  </div>

                  <Button
                    className={
                      'mt-3 w-full h-8 rounded-lg bg-primary-800 hover:!bg-primary-900 text-primary-50 hover:!text-primary-50 font-semibold border-none'
                    }
                  >
                    View more
                  </Button>
                </div>
              </Link>
            ))}
          </div>

          {/* RIGHT ARROW (Desktop only) */}
          <button
            onClick={() => scrollByCards(1)}
            className={'hidden lg:flex items-center justify-center w-8 h-8 rounded-full  shadow hover:bg-gray-100'}
          >
            <RightOutlined className={'text-primary-900 text-xl font-bold'} />
          </button>
        </div>
      </div>
    </div>
  );
};

RelatedProducts.propTypes = {
  products: PropTypes.array.isRequired,
  currentProduct: PropTypes.object.isRequired,
};

export default RelatedProducts;
